import { apiMiddleware } from './apiMiddleware'

// TypeScript interfaces for the simplified Lead model
export interface Lead {
  id: string
  first_name: string
  last_name: string
  name: string
  phone: string
  // COMMENTED OUT - Email field temporarily disabled
  email?: string
  source: string
  status?: string
  assigned_to_attorney?: {
    id: string
    name: string
    email: string
    phone: string
  } | null
  referred_to_firm?: {
    id: string
    name: string
    law_firm: string
  } | null
  created_by?: any
  created_at?: string
  comment_count?: number
  last_comment?: {
    id: string
    comment: string
    commented_on: string
    commented_by: string
    commented_by_display: string
  }
  unread_sms_count?: number  // Count of unread SMS from this lead
}

export interface Comment {
  id: string
  comment: string
  commented_on: string
  commented_by: any
  commented_by_display: string
  lead: string
}

export interface CreateLeadData {
  first_name: string
  last_name: string
  phone: string
  // COMMENTED OUT - Email field temporarily disabled
  email?: string
  source: string
  comments?: string[]
}

export interface UpdateLeadData {
  first_name?: string
  last_name?: string
  phone?: string
  email?: string
  source?: string
  status?: string
  assigned_to_attorney?: string | null
  referred_to_firm?: string | null
}

export interface LeadsResponse {
  leads: Lead[]
  total_count: number
  offset: number
}

export interface LeadDetailResponse {
  lead: Lead
  comments: Comment[]
}

export interface CommentsResponse {
  error: boolean
  comments: Comment[]
  message?: string
}

export interface ApiError {
  error: boolean
  errors?: any
  message?: string
}

export interface ApiSuccess {
  error: boolean
  message: string
  lead_id?: string
}

export interface StatusHistory {
  id: string
  old_status: string | null
  new_status: string
  changed_by_user: string | null
  changed_at: string
  message: string | null
}

export interface StatusHistoryResponse {
  error: boolean
  status_history: StatusHistory[]
}

export interface Activity {
  id: string
  activity_type: string
  title: string
  description: string
  timestamp: string
  user: string | null
}

export interface ActivityResponse {
  error: boolean
  activities: Activity[]
}

class LeadsApiService {
  // No need for baseUrl or makeRequest - using middleware

  // Get all leads with pagination
  async getLeads(page: number = 1, limit: number = 10, search: string = '', filters?: any): Promise<LeadsResponse> {
    const params = new URLSearchParams()
    
    // Pagination parameters
    const offset = (page - 1) * limit
    params.append('limit', limit.toString())
    params.append('offset', offset.toString())
    
    // Search parameter
    if (search) {
      params.append('search', search)
    }
    
    // Filter parameters
    if (filters?.status) {
      params.append('status', filters.status)
    }
    if (filters?.source) {
      params.append('source', filters.source)
    }

    return apiMiddleware.get<LeadsResponse>(`leads/?${params.toString()}`)
  }

  // Get a single lead by ID
  async getLead(id: string): Promise<LeadDetailResponse> {
    return apiMiddleware.get<LeadDetailResponse>(`leads/${id}/`)
  }

  // Create a new lead
  async createLead(data: CreateLeadData): Promise<ApiSuccess> {
    return apiMiddleware.post<ApiSuccess>('leads/', data)
  }

  // Update an existing lead
  async updateLead(id: string, data: UpdateLeadData): Promise<ApiSuccess> {
    return apiMiddleware.patch<ApiSuccess>(`leads/${id}/`, data)
  }

  // Delete a lead
  async deleteLead(id: string): Promise<ApiSuccess> {
    return apiMiddleware.delete<ApiSuccess>(`leads/${id}/`)
  }

  // Check if a lead email is available
  async checkLeadEmailAvailability(email: string, excludeLeadId?: string): Promise<{ available: boolean; message: string }> {
    const data: any = { email }
    if (excludeLeadId) {
      data.exclude_lead_id = excludeLeadId
    }
    
    return apiMiddleware.post<{ available: boolean; message: string }>('leads/check-email/', data)
  }

  // Check if a lead phone number is available
  async checkLeadPhoneAvailability(phone: string, excludeLeadId?: string): Promise<{ available: boolean; message: string }> {
    const data: any = { phone }
    if (excludeLeadId) {
      data.exclude_lead_id = excludeLeadId
    }
    
    return apiMiddleware.post<{ available: boolean; message: string }>('leads/check-phone/', data)
  }

  // Archive a lead
  async archiveLead(id: string): Promise<ApiSuccess> {
    return apiMiddleware.patch<ApiSuccess>(`leads/${id}/`, { status: 'archived' })
  }

  // Add a comment to a lead
  async addComment(leadId: string, comment: string): Promise<ApiSuccess> {
    return apiMiddleware.post<ApiSuccess>(`leads/${leadId}/comments/`, { comment })
  }

  // Get comments for a lead
  async getLeadComments(leadId: string): Promise<CommentsResponse> {
    return apiMiddleware.get<CommentsResponse>(`leads/${leadId}/comments/`)
  }

  // Update a comment
  async updateComment(commentId: string, comment: string): Promise<ApiSuccess> {
    return apiMiddleware.put<ApiSuccess>(`leads/comment/${commentId}/`, { comment })
  }

  // Delete a comment
  async deleteComment(commentId: string): Promise<ApiSuccess> {
    return apiMiddleware.delete<ApiSuccess>(`leads/comment/${commentId}/`)
  }

  // Delete an attachment
  async deleteAttachment(attachmentId: string): Promise<ApiSuccess> {
    return apiMiddleware.delete<ApiSuccess>(`leads/attachment/${attachmentId}/`)
  }

  // Upload leads from file
  async uploadLeads(file: File): Promise<ApiSuccess> {
    const formData = new FormData()
    formData.append('leads_file', file)

    // For file uploads, we need to use the raw fetch with FormData
    // The middleware doesn't handle FormData bodies properly
    return apiMiddleware.makeRequest<ApiSuccess>('leads/upload/', {
      method: 'POST',
      body: formData,
      headers: {} // Let browser set Content-Type for FormData
    }, { retryOnAuth: true })
  }

  // Create lead from site (external API)
  async createLeadFromSite(data: CreateLeadData & { apikey: string }): Promise<ApiSuccess> {
    return apiMiddleware.post<ApiSuccess>('leads/create-from-site/', data)
  }

  // Assign lead to attorney
  async assignToAttorney(leadId: string, attorneyId: string): Promise<ApiSuccess> {
    return apiMiddleware.patch<ApiSuccess>(`leads/${leadId}/`, { 
      assigned_to_attorney: attorneyId,
      referred_to_firm: null, // Clear firm assignment
      status: 'assigned'
    })
  }

  // Refer lead to firm
  async referToFirm(leadId: string, firmId: string): Promise<ApiSuccess> {
    return apiMiddleware.patch<ApiSuccess>(`leads/${leadId}/`, { 
      referred_to_firm: firmId,
      assigned_to_attorney: null, // Clear attorney assignment
      status: 'referred'
    })
  }

  // Remove assignment (unassign from both attorney and firm)
  async removeAssignment(leadId: string): Promise<ApiSuccess> {
    return apiMiddleware.patch<ApiSuccess>(`leads/${leadId}/`, { 
      assigned_to_attorney: null,
      referred_to_firm: null,
      status: 'new'
    })
  }

  // Get status history for a lead
  async getLeadStatusHistory(leadId: string): Promise<StatusHistory[]> {
    const response = await apiMiddleware.get<StatusHistoryResponse>(`leads/${leadId}/status-history/`)
    return response.status_history
  }

  // Get activities for a lead
  async getLeadActivities(leadId: string): Promise<Activity[]> {
    const response = await apiMiddleware.get<ActivityResponse>(`leads/${leadId}/activities/`)
    return response.activities
  }
}

// Export a singleton instance
export const leadsApi = new LeadsApiService() 